import type { MaintenanceContractIssue } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import {
  MAINTENANCE_SLA_ON_SITE_LEVELS,
  MAINTENANCE_SLA_REMOTE_LEVELS,
  type MaintenanceSlaInterventionMode,
  type MaintenanceSlaLevelCode,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';

export type MaintenanceFormTabKey = 'general' | 'onSite' | 'remote' | 'scope';

const TAB_ORDER: MaintenanceFormTabKey[] = [
  'general',
  'onSite',
  'remote',
  'scope',
];

function isOnSiteLevel(code: MaintenanceSlaLevelCode): boolean {
  return (MAINTENANCE_SLA_ON_SITE_LEVELS as readonly string[]).includes(code);
}

function isRemoteLevel(code: MaintenanceSlaLevelCode): boolean {
  return (MAINTENANCE_SLA_REMOTE_LEVELS as readonly string[]).includes(code);
}

export function maintenanceFormTabForIssue(
  issue: MaintenanceContractIssue,
): MaintenanceFormTabKey {
  if (
    issue.code === 'scopeValue' ||
    issue.code === 'scopeInterventionModeRequired' ||
    issue.code === 'scopeHierarchyRedundancy' ||
    issue.code === 'scopeEffectConflict'
  ) {
    return 'scope';
  }
  if (issue.levelCode != null) {
    if (isOnSiteLevel(issue.levelCode)) {
      return 'onSite';
    }
    if (isRemoteLevel(issue.levelCode)) {
      return 'remote';
    }
  }
  return 'general';
}

export function countMaintenanceFormTabIssues(
  issues: readonly MaintenanceContractIssue[],
  tab: MaintenanceFormTabKey,
): number {
  return issues.filter((issue) => maintenanceFormTabForIssue(issue) === tab)
    .length;
}

export function maintenanceFormTabsForInterventionMode(
  mode: MaintenanceSlaInterventionMode | null,
): MaintenanceFormTabKey[] {
  const showOnSite = mode == null || mode === 'BOTH' || mode === 'ONSITE';
  const showRemote = mode == null || mode === 'BOTH' || mode === 'REMOTE';

  return TAB_ORDER.filter((tab) => {
    if (tab === 'onSite') {
      return showOnSite;
    }
    if (tab === 'remote') {
      return showRemote;
    }
    return true;
  });
}

export function firstMaintenanceFormTabWithIssues(
  issues: readonly MaintenanceContractIssue[],
  mode: MaintenanceSlaInterventionMode | null,
): MaintenanceFormTabKey | null {
  const tabs = maintenanceFormTabsForInterventionMode(mode);
  for (const tab of TAB_ORDER) {
    if (!tabs.includes(tab)) {
      continue;
    }
    if (countMaintenanceFormTabIssues(issues, tab) > 0) {
      return tab;
    }
  }
  return null;
}
